import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import NavMenu from '../components/ui/nav-menu';
import FooterNav from '../components/ui/footer-nav';
import ModuleRail from '../components/ui/module-rail';
import HeaderTasksButton from '../components/ui/header-tasks-button';
import { Mic, MicOff, X, FileText, Loader2, ChevronDown, Trophy, Plus } from 'lucide-react';
import { toast } from 'sonner';
import MicrophoneHelp from '../components/ui/microphone-help';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// WIN WORK = Green (Emerald) theme
const STAGE = {
  bar: 'bg-emerald-500',
  label: 'text-emerald-300',
  button: 'bg-emerald-600 hover:bg-emerald-700',
  mic: 'bg-emerald-600 border-emerald-400 hover:bg-emerald-700',
  accent: 'text-emerald-600',
  lightBg: 'bg-emerald-50',
  border: 'border-emerald-300',
};

const CaptureJobNotes = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [micError, setMicError] = useState('');
  const recognitionRef = useRef(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    job_name: '',
    job_notes: '',
    approximate_area: '',
    key_materials: '',
    specific_requests: '',
    access_conditions: ''
  });

  const [attachments, setAttachments] = useState([]);
  const [generatingScope, setGeneratingScope] = useState(false);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      setSpeechSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-AU';
      
      recognition.onresult = (event) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          }
        }
        if (finalTranscript) {
          setFormData(prev => ({
            ...prev,
            job_notes: prev.job_notes + finalTranscript
          }));
        }
      };
      
      recognition.onerror = (event) => {
        setIsListening(false);
        if (event.error === 'not-allowed') {
          setMicError('Microphone blocked. Allow access in browser settings.');
        } else if (event.error !== 'no-speech') {
          setMicError('Could not access microphone. Try typing instead.');
        }
      };
      
      recognition.onend = () => setIsListening(false);
      recognitionRef.current = recognition;
    }
    return () => { if (recognitionRef.current) recognitionRef.current.stop(); };
  }, []);

  const toggleListening = async () => {
    setMicError('');
    if (!speechSupported) {
      setMicError('Speech not supported. Use keyboard mic instead.');
      return;
    }
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
        recognitionRef.current.start();
        setIsListening(true);
      } catch (error) {
        setMicError(error.name === 'NotAllowedError' 
          ? 'Microphone blocked. Tap lock icon to allow.' 
          : 'Could not start microphone.');
      }
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (attachments.length + files.length > 5) {
      toast.error('Maximum 5 attachments');
      return;
    }
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} too large (max 10MB)`);
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        setAttachments(prev => [...prev, { data: e.target.result, name: file.name, type: file.type, file }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (index) => setAttachments(prev => prev.filter((_, i) => i !== index));

  const handleCreateDraftScope = async () => {
    if (!formData.job_notes.trim()) {
      toast.error('Please enter job notes');
      return;
    }
    setGeneratingScope(true);
    try {
      const webhookUrl = process.env.REACT_APP_DRAFT_SCOPE_WEBHOOK_URL;
      const payload = {
        job_notes: formData.job_notes,
        approximate_area: formData.approximate_area,
        key_materials_system: formData.key_materials,
        specific_requests: formData.specific_requests,
        access_site_conditions: formData.access_conditions
      };
      const response = await axios.post(webhookUrl, payload, { headers: { 'Content-Type': 'application/json' } });
      if (response.data.status === 'success') {
        toast.success('Draft scope generated!');
        sessionStorage.setItem('draftScopeResult', JSON.stringify(response.data));
        navigate('/tradie/draft-scope-result');
      } else {
        toast.error('Failed to generate scope');
      }
    } catch (error) {
      toast.error('Connection failed. Please try again.');
    } finally {
      setGeneratingScope(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <header className="bg-[#1F2A37] text-white p-4 sticky top-0 z-10">
        <div className={`h-1 ${STAGE.bar} absolute top-0 left-0 right-0`}></div>
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Trophy className="w-7 h-7 text-emerald-400" />
            <div>
              <h1 className="text-lg font-bold">Generate Scope</h1>
              <p className={`${STAGE.label} text-xs font-medium`}>WIN WORK</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <HeaderTasksButton stage="win" />
            <NavMenu />
          </div>
        </div>
      </header>

      {/* Module Rail */}
      <ModuleRail stage="win" activeModule="scope" />

      {/* Form */}
      <main className="p-3 max-w-lg mx-auto pb-6">
        <Card className="border-2 border-slate-200">
          <CardContent className="p-4 space-y-4">
            
            <p className="text-slate-600 text-sm">Turn rough notes into a draft scope.</p>

            {/* Job Name */}
            <div className="space-y-1">
              <Label className="text-sm font-bold text-slate-700">Job name <span className="font-normal text-slate-400">(optional)</span></Label>
              <Input data-testid="job-name-input" placeholder="Project name" value={formData.job_name} onChange={(e) => handleChange('job_name', e.target.value)} className="h-11 border-2" />
            </div>

            {/* Mic */}
            <div className="flex flex-col items-center py-2">
              <button
                type="button"
                data-testid="mic-button"
                onClick={toggleListening}
                className={`w-14 h-14 flex items-center justify-center rounded-full transition-all border-4 ${
                  isListening ? 'bg-red-500 border-red-300 animate-pulse' : `${STAGE.mic} active:scale-95`
                }`}
              >
                {isListening ? <MicOff className="w-7 h-7 text-white" strokeWidth={2} /> : <Mic className="w-7 h-7 text-white" strokeWidth={2} />}
              </button>
              <p className="text-center text-xs mt-2 font-medium">
                {isListening ? <span className="text-red-600">Listening... Tap to stop</span> : <span className="text-slate-500">Speak your notes</span>}
              </p>
              {micError && <MicrophoneHelp error={micError} className="mt-2 max-w-xs" />}
            </div>

            {/* Notes */}
            <div className="space-y-1">
              <Label className="text-sm font-bold text-slate-700">What needs to be scoped? <span className="text-red-500">*</span></Label>
              <Textarea data-testid="job-notes-input" placeholder="e.g. Front render 120sqm, acrylic finish, repair cracks" value={formData.job_notes} onChange={(e) => handleChange('job_notes', e.target.value)} className="min-h-[90px] text-base border-2" required />
            </div>

            {/* Attachments */}
            <div className="space-y-2">
              <input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx" multiple onChange={handleFileSelect} className="hidden" data-testid="attachment-input" />
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {attachments.map((att, i) => (
                    <div key={i} className="relative w-12 h-12 rounded-lg overflow-hidden border-2 border-slate-200 bg-slate-50">
                      {att.type.startsWith('image/') ? <img src={att.data} alt={att.name} className="w-full h-full object-cover" /> : <FileText className="w-5 h-5 text-slate-400 m-auto mt-3" />}
                      <button type="button" onClick={() => removeAttachment(i)} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
              {attachments.length < 5 && (
                <button type="button" data-testid="add-attachment-btn" onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 py-1">
                  <div className="w-7 h-7 rounded-full border-2 border-dashed border-slate-300 flex items-center justify-center"><Plus className="w-4 h-4" /></div>
                  <span>Add attachment</span>
                  <span className="text-slate-400 text-xs">(optional)</span>
                </button>
              )}
            </div>

            {/* Optional Details */}
            <Collapsible open={detailsOpen} onOpenChange={setDetailsOpen}>
              <CollapsibleTrigger asChild>
                <button type="button" data-testid="toggle-details-btn" className={`flex items-center justify-between w-full py-2.5 px-3 rounded-lg border-2 transition-colors ${detailsOpen ? `${STAGE.lightBg} ${STAGE.border}` : `${STAGE.lightBg} ${STAGE.border} hover:bg-emerald-100/50`}`}>
                  <span className={`text-sm font-semibold ${STAGE.accent}`}>+ Add more detail to improve draft</span>
                  <ChevronDown className={`w-5 h-5 transition-transform ${detailsOpen ? `rotate-180 ${STAGE.accent}` : STAGE.accent}`} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3 space-y-3">
                <div className="space-y-1">
                  <Label className="text-xs font-bold text-slate-600">Area (sqm)</Label>
                  <Input data-testid="area-input" placeholder="e.g. 120" value={formData.approximate_area} onChange={(e) => handleChange('approximate_area', e.target.value)} className="h-10 border-2" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-bold text-slate-600">Materials / System</Label>
                  <Input data-testid="materials-input" placeholder="e.g. acrylic render" value={formData.key_materials} onChange={(e) => handleChange('key_materials', e.target.value)} className="h-10 border-2" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-bold text-slate-600">Specific requests</Label>
                  <Input data-testid="requests-input" placeholder="e.g. match existing finish" value={formData.specific_requests} onChange={(e) => handleChange('specific_requests', e.target.value)} className="h-10 border-2" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-bold text-slate-600">Access / Site conditions</Label>
                  <Input data-testid="access-input" placeholder="e.g. scaffold required" value={formData.access_conditions} onChange={(e) => handleChange('access_conditions', e.target.value)} className="h-10 border-2" />
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Submit */}
            <Button type="button" data-testid="create-draft-scope-btn" onClick={handleCreateDraftScope} disabled={generatingScope} className={`w-full h-12 ${STAGE.button} text-white text-base font-bold`}>
              {generatingScope ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generating...</> : <><FileText className="w-5 h-5 mr-2" strokeWidth={2} />Create Draft Scope</>}
            </Button>

          </CardContent>
        </Card>
      </main>
      
      <FooterNav />
    </div>
  );
};

export default CaptureJobNotes;
